/**
 * Needs Review page — /needs-review
 *
 * Split-panel layout: document preview (left) + extraction fields (right).
 * Navigate through artifacts flagged for review, manually correct extraction
 * data, and accept/requeue.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Download,
  FileText,
  User,
  Vote,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/hooks/useUser';
import { usePageTitle } from '@/hooks/usePageTitle';
import { apiFetch } from '@/lib/apiFetch';

interface ReviewArtifact {
  id: string;
  name: string;
  meeting_date: string | null;
  governing_body: string | null;
  meeting_type: string | null;
  kind: string;
  url: string;
  source_name: string;
  // Flags
  needs_text_review: boolean;
  needs_attendees_review: boolean;
  needs_motions_review: boolean;
  needs_financial_review: boolean;
  // Extracted data
  extracted_text: string | null;
  text_quality_score: number | null;
  attendees: { id?: string; name: string; status?: string }[];
  motions: {
    id?: string;
    motion_text: string;
    moved_by: string | null;
    seconded_by: string | null;
    outcome: string | null;
    instrument_type: string | null;
    instrument_number: string | null;
    instrument_title: string | null;
  }[];
  financial: {
    id?: string;
    action_type: string;
    amount: number | null;
    direction: string;
    vendor_payee: string | null;
    description: string | null;
  }[];
  classification: {
    meeting_type: string | null;
    meeting_date: string | null;
    governing_body: string | null;
    department: string | null;
  };
  audit_log: {
    action: string;
    field_name: string | null;
    old_value: string | null;
    new_value: string | null;
    created_at: string;
    user: string;
  }[];
}

type ReviewTab = 'all' | 'text' | 'attendees' | 'motions' | 'financial';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(s: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function reviewBadge(label: string, active: boolean) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        active
          ? 'bg-amber-100 text-amber-700 border border-amber-200'
          : 'bg-gray-100 text-gray-500 border border-gray-200'
      }`}
    >
      {active && <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

// ── Document Preview ──────────────────────────────────────────────────────────

function DocumentPreview({ artifact }: { artifact: ReviewArtifact | null }) {
  const [loaded, setLoaded] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);

  const artifactId = artifact?.id;

  useEffect(() => {
    if (!artifactId) return;
    let cancelled = false;
    // Defer state updates to avoid sync setState in effect
    queueMicrotask(() => {
      if (cancelled) return;
      setUrlLoading(true);
      setLoaded(false);
      apiFetch(`/api/artifacts/${artifactId}/download?download=false`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((json) => {
          if (!cancelled && json.data?.url) {
            setSignedUrl(json.data.url);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.warn(
              `[DocumentPreview] failed to get signed URL for ${artifactId}:`,
              err instanceof Error ? err.message : err
            );
          }
        })
        .finally(() => {
          if (!cancelled) setUrlLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [artifactId]);

  if (!artifact) {
    return null;
  }

  const isPdf =
    artifact.kind === 'pdf' &&
    !artifact.url.match(/[?&](ical|outlook-ical)=1/) &&
    !/\.(ics|ical|icalendar)$/i.test(artifact.url.split('?')[0]);

  // Get download URL — fetch on click to get fresh signed URL
  const handleDownload = async () => {
    try {
      const res = await apiFetch(
        `/api/artifacts/${artifact.id}/download?download=true`
      );
      const json = await res.json();
      if (json.data?.url) window.open(json.data.url, '_blank');
    } catch {
      // ignore
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-xs text-gray-500 truncate flex-1">
          {artifact.name || artifact.url}
        </span>
        <button
          onClick={handleDownload}
          className="ml-2 text-gray-400 hover:text-gray-600"
          title="Download original"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 relative">
        {isPdf && signedUrl ? (
          <>
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            )}
            <iframe
              src={signedUrl}
              className="w-full h-full border-0"
              onLoad={() => setLoaded(true)}
              title={artifact.name || 'Document preview'}
            />
          </>
        ) : urlLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : artifact.extracted_text ? (
          <div className="p-4 overflow-auto h-full">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
              {artifact.extracted_text}
            </pre>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
            <FileText className="w-10 h-10" />
            <span className="text-sm">Preview not available</span>
            <span className="text-xs">Download original to view</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Extraction Fields Panel ───────────────────────────────────────────────────

function ExtractionPanel({
  artifact,
  onAccept,
  onSaveDraft,
  onRequeue,
  onUpdateField,
  busy,
}: {
  artifact: ReviewArtifact | null;
  onAccept: () => void;
  onSaveDraft: () => void;
  onRequeue: (stage: string) => void;
  onUpdateField: (field: string, value: unknown) => void;
  busy: boolean;
}) {
  // Local editable state — initialized from artifact prop, reset by component key
  const [editableAttendees, setEditableAttendees] = useState<
    { name: string; status: string }[]
  >(
    (artifact?.attendees ?? []).map((a: { name: string; status?: string }) => ({
      name: a.name,
      status: a.status ?? 'present',
    }))
  );
  const [editableMotions, setEditableMotions] = useState<
    {
      motion_text: string;
      moved_by: string | null;
      seconded_by: string | null;
      outcome: string | null;
      instrument_type: string | null;
      instrument_number: string | null;
      instrument_title: string | null;
    }[]
  >(
    (artifact?.motions ?? []).map(
      (m: {
        motion_text: string;
        moved_by: string | null;
        seconded_by: string | null;
        outcome: string | null;
        instrument_type?: string | null;
        instrument_number?: string | null;
        instrument_title?: string | null;
      }) => ({
        motion_text: m.motion_text,
        moved_by: m.moved_by,
        seconded_by: m.seconded_by,
        outcome: m.outcome,
        instrument_type: m.instrument_type ?? null,
        instrument_number: m.instrument_number ?? null,
        instrument_title: m.instrument_title ?? null,
      })
    )
  );
  const [editableFinancial, setEditableFinancial] = useState<
    {
      action_type: string;
      amount: number | null;
      direction: string;
      vendor_payee: string | null;
      description: string | null;
    }[]
  >(
    (artifact?.financial ?? []).map(
      (f: {
        action_type: string;
        amount: number | null;
        direction: string;
        vendor_payee: string | null;
        description: string | null;
      }) => ({
        action_type: f.action_type,
        amount: f.amount,
        direction: f.direction,
        vendor_payee: f.vendor_payee,
        description: f.description,
      })
    )
  );

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    classification: true,
    text: artifact?.needs_text_review ?? false,
    attendees: artifact?.needs_attendees_review ?? false,
    motions: artifact?.needs_motions_review ?? false,
    financial: artifact?.needs_financial_review ?? false,
  });

  function toggleSection(key: string) {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  if (!artifact) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Select an artifact to review
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-sm truncate">
            {artifact.name || artifact.url?.split('/').pop()}
          </h3>
        </div>
        <div className="text-xs text-gray-500">
          {artifact.source_name} · {fmtDate(artifact.meeting_date)}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {reviewBadge('Text', artifact.needs_text_review)}
          {reviewBadge('Attendees', artifact.needs_attendees_review)}
          {reviewBadge('Motions', artifact.needs_motions_review)}
          {reviewBadge('Financial', artifact.needs_financial_review)}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Classification */}
        <Section
          title="Classification"
          icon={<Calendar className="w-4 h-4" />}
          expanded={expandedSections.classification}
          onToggle={() => toggleSection('classification')}
        >
          <EditableField
            label="Meeting Type"
            value={artifact.classification?.meeting_type ?? ''}
            onChange={(v) => onUpdateField('meeting_type', v)}
          />
          <EditableField
            label="Meeting Date"
            value={artifact.classification?.meeting_date ?? ''}
            onChange={(v) => onUpdateField('meeting_date', v)}
          />
          <EditableField
            label="Governing Body"
            value={artifact.classification?.governing_body ?? ''}
            onChange={(v) => onUpdateField('governing_body', v)}
          />
          <EditableField
            label="Department"
            value={artifact.classification?.department ?? ''}
            onChange={(v) => onUpdateField('department', v)}
          />
        </Section>

        {/* Text quality */}
        {artifact.needs_text_review && (
          <Section
            title="Text Quality"
            icon={<FileText className="w-4 h-4" />}
            expanded={expandedSections.text}
            onToggle={() => toggleSection('text')}
            badge={
              artifact.text_quality_score != null
                ? `Score: ${artifact.text_quality_score}`
                : undefined
            }
          >
            <div className="text-xs text-gray-500 mb-2">
              {artifact.extracted_text
                ? `Extracted ${artifact.extracted_text.length} characters`
                : 'No text extracted'}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequeue('text')}
              className="w-full"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry Extraction
            </Button>
          </Section>
        )}

        {/* Attendees — always visible, review flag as badge */}
        <Section
          title="Attendees"
          icon={<User className="w-4 h-4" />}
          expanded={expandedSections.attendees}
          onToggle={() => toggleSection('attendees')}
          badge={artifact.needs_attendees_review ? 'needs review' : undefined}
          badgeStyle={artifact.needs_attendees_review ? 'warn' : undefined}
        >
          {editableAttendees.length === 0 ? (
            <p className="text-xs text-gray-500">No attendees extracted</p>
          ) : (
            <ul className="space-y-1">
              {editableAttendees.map((a, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-sm text-gray-700 py-1"
                >
                  <input
                    type="text"
                    value={a.name}
                    onChange={(e) => {
                      const updated = [...editableAttendees];
                      updated[i] = { ...updated[i], name: e.target.value };
                      setEditableAttendees(updated);
                    }}
                    placeholder="Name"
                    className="flex-1 min-w-0 px-2 py-0.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <select
                    value={a.status}
                    onChange={(e) => {
                      const updated = [...editableAttendees];
                      updated[i] = { ...updated[i], status: e.target.value };
                      setEditableAttendees(updated);
                    }}
                    className="w-24 px-1.5 py-0.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="excused">Excused</option>
                    <option value="unknown">Unknown</option>
                  </select>
                  <button
                    onClick={() => {
                      setEditableAttendees(
                        editableAttendees.filter((_, j) => j !== i)
                      );
                    }}
                    className="text-gray-400 hover:text-red-500 shrink-0"
                    title="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setEditableAttendees([
                  ...editableAttendees,
                  { name: '', status: 'present' },
                ])
              }
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              + Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequeue('attendees')}
              title="Re-enqueue the extraction worker to retry this stage"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </Section>

        {/* Motions — always visible */}
        <Section
          title="Motions"
          icon={<Vote className="w-4 h-4" />}
          expanded={expandedSections.motions}
          onToggle={() => toggleSection('motions')}
          badge={artifact.needs_motions_review ? 'needs review' : undefined}
          badgeStyle={artifact.needs_motions_review ? 'warn' : undefined}
        >
          {editableMotions.length === 0 ? (
            <p className="text-xs text-gray-500">No motions extracted</p>
          ) : (
            <div className="space-y-2">
              {editableMotions.map((m, i) => (
                <div key={i} className="border rounded p-2 space-y-1">
                  <EditableField
                    label="Text"
                    value={m.motion_text}
                    onChange={(v) => {
                      const updated = [...editableMotions];
                      updated[i] = { ...updated[i], motion_text: v };
                      setEditableMotions(updated);
                    }}
                  />
                  <EditableField
                    label="Moved by"
                    value={m.moved_by ?? ''}
                    onChange={(v) => {
                      const updated = [...editableMotions];
                      updated[i] = { ...updated[i], moved_by: v };
                      setEditableMotions(updated);
                    }}
                  />
                  <EditableField
                    label="Seconded by"
                    value={m.seconded_by ?? ''}
                    onChange={(v) => {
                      const updated = [...editableMotions];
                      updated[i] = { ...updated[i], seconded_by: v };
                      setEditableMotions(updated);
                    }}
                  />
                  <EditableField
                    label="Outcome"
                    value={m.outcome ?? ''}
                    type="select"
                    selectOptions={[
                      { value: '', label: '—' },
                      { value: 'carried', label: 'Carried' },
                      { value: 'failed', label: 'Failed' },
                      { value: 'tabled', label: 'Tabled' },
                      { value: 'unknown', label: 'Unknown' },
                    ]}
                    onChange={(v) => {
                      const updated = [...editableMotions];
                      updated[i] = { ...updated[i], outcome: v };
                      setEditableMotions(updated);
                    }}
                  />
                  {m.instrument_type ? (
                    <>
                      <div className="text-xs text-gray-400 font-medium pt-1 pb-0.5">
                        Resolution / Ordinance
                      </div>
                      <EditableField
                        label="Type"
                        value={m.instrument_type ?? ''}
                        type="select"
                        selectOptions={[
                          { value: '', label: '—' },
                          { value: 'resolution', label: 'Resolution' },
                          { value: 'ordinance', label: 'Ordinance' },
                        ]}
                        onChange={(v) => {
                          const updated = [...editableMotions];
                          updated[i] = {
                            ...updated[i],
                            instrument_type: v || null,
                          };
                          setEditableMotions(updated);
                        }}
                      />
                      <EditableField
                        label="Number"
                        value={m.instrument_number ?? ''}
                        onChange={(v) => {
                          const updated = [...editableMotions];
                          updated[i] = {
                            ...updated[i],
                            instrument_number: v || null,
                          };
                          setEditableMotions(updated);
                        }}
                      />
                      <EditableField
                        label="Title"
                        value={m.instrument_title ?? ''}
                        onChange={(v) => {
                          const updated = [...editableMotions];
                          updated[i] = {
                            ...updated[i],
                            instrument_title: v || null,
                          };
                          setEditableMotions(updated);
                        }}
                      />
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const updated = [...editableMotions];
                        updated[i] = {
                          ...updated[i],
                          instrument_type: 'resolution',
                          instrument_number: '',
                          instrument_title: '',
                        };
                        setEditableMotions(updated);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      + Add Resolution/Ordinance
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setEditableMotions([
                  ...editableMotions,
                  {
                    motion_text: '',
                    moved_by: null,
                    seconded_by: null,
                    outcome: null,
                    instrument_type: null,
                    instrument_number: null,
                    instrument_title: null,
                  },
                ])
              }
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              + Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequeue('motions')}
              title="Re-enqueue the extraction worker to retry this stage"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </Section>

        {/* Financial — always visible */}
        <Section
          title="Financial"
          icon={<DollarSign className="w-4 h-4" />}
          expanded={expandedSections.financial}
          onToggle={() => toggleSection('financial')}
          badge={artifact.needs_financial_review ? 'needs review' : undefined}
          badgeStyle={artifact.needs_financial_review ? 'warn' : undefined}
        >
          {editableFinancial.length === 0 ? (
            <p className="text-xs text-gray-500">No financial data extracted</p>
          ) : (
            <div className="space-y-2">
              {editableFinancial.map((f, i) => (
                <div key={i} className="border rounded p-2 space-y-1">
                  <EditableField
                    label="Type"
                    value={f.action_type ?? ''}
                    onChange={(v) => {
                      const updated = [...editableFinancial];
                      updated[i] = { ...updated[i], action_type: v };
                      setEditableFinancial(updated);
                    }}
                  />
                  <EditableField
                    label="Amount"
                    value={f.amount != null ? String(f.amount) : ''}
                    onChange={(v) => {
                      const updated = [...editableFinancial];
                      updated[i] = {
                        ...updated[i],
                        amount: v ? parseFloat(v) : null,
                      };
                      setEditableFinancial(updated);
                    }}
                  />
                  <EditableField
                    label="Direction"
                    value={f.direction ?? ''}
                    onChange={(v) => {
                      const updated = [...editableFinancial];
                      updated[i] = { ...updated[i], direction: v };
                      setEditableFinancial(updated);
                    }}
                  />
                  <EditableField
                    label="Vendor/Payee"
                    value={f.vendor_payee ?? ''}
                    onChange={(v) => {
                      const updated = [...editableFinancial];
                      updated[i] = { ...updated[i], vendor_payee: v };
                      setEditableFinancial(updated);
                    }}
                  />
                  <EditableField
                    label="Description"
                    value={f.description ?? ''}
                    onChange={(v) => {
                      const updated = [...editableFinancial];
                      updated[i] = { ...updated[i], description: v };
                      setEditableFinancial(updated);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setEditableFinancial([
                  ...editableFinancial,
                  {
                    action_type: '',
                    amount: null,
                    direction: 'spend',
                    vendor_payee: null,
                    description: null,
                  },
                ])
              }
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              + Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRequeue('financial')}
              title="Re-enqueue the extraction worker to retry this stage"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </Section>
      </div>

      {/* Save / Submit buttons */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white space-y-2">
        <Button
          onClick={onSaveDraft}
          disabled={busy}
          className="w-full"
          variant="outline"
        >
          <Save className="w-4 h-4 mr-1" />
          Save Draft
        </Button>
        <Button
          onClick={onAccept}
          disabled={busy}
          className="w-full"
          variant="default"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-1" />
          )}
          Submit Final
        </Button>
      </div>

      {/* Audit log */}
      {artifact.audit_log && artifact.audit_log.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-1">
            Review History
          </p>
          <div className="space-y-1">
            {artifact.audit_log.slice(0, 5).map((entry, i) => (
              <div
                key={i}
                className="text-xs text-gray-500 flex justify-between"
              >
                <span>
                  <span className="text-gray-700">{entry.user}</span>{' '}
                  {entry.field_name
                    ? `changed ${entry.field_name}`
                    : entry.action === 'accept'
                      ? 'accepted'
                      : entry.action}
                </span>
                <span>{new Date(entry.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Collapsible Section ───────────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
  expanded,
  onToggle,
  badge,
  badgeStyle,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  badge?: string;
  badgeStyle?: string;
}) {
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-left"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          {icon}
          {title}
          {badge && (
            <span
              className={
                badgeStyle === 'warn'
                  ? 'text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium'
                  : 'text-xs text-gray-400 ml-1'
              }
            >
              {badge}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expanded && <div className="px-4 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

// ── Editable Field ────────────────────────────────────────────────────────────

function EditableField({
  label,
  value,
  onChange,
  type,
  selectOptions,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'select';
  selectOptions?: { value: string; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-500 w-28 shrink-0 text-xs">{label}</span>
      {type === 'select' && selectOptions ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
        >
          {selectOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export const NeedsReviewPage: React.FC = () => {
  usePageTitle('Artifacts Review');
  const { user, loading: userLoading } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialIdx = parseInt(searchParams.get('idx') ?? '0', 10);
  const [artifacts, setArtifacts] = useState<ReviewArtifact[]>([]);
  const [currentIdx, setCurrentIdx] = useState(
    isNaN(initialIdx) || initialIdx < 0 ? 0 : initialIdx
  );
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReviewTab>('all');

  // Sync URL with current index
  useEffect(() => {
    if (
      artifacts.length > 0 &&
      currentIdx >= 0 &&
      currentIdx < artifacts.length
    ) {
      setSearchParams({ idx: String(currentIdx) }, { replace: true });
    } else if (artifacts.length === 0) {
      setSearchParams({}, { replace: true });
    }
  }, [currentIdx, artifacts.length, setSearchParams]);

  const fetchArtifacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/needs-review${filter !== 'all' ? `?type=${filter}` : ''}`
      );
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const json = await res.json();
      let data = (json.data ?? []) as ReviewArtifact[];
      if (filter === 'text') {
        data = data.filter((a: ReviewArtifact) => a.needs_text_review);
      }
      setArtifacts(data);
      if (filter !== 'all' || searchParams.get('idx') == null) {
        setCurrentIdx(0);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void fetchArtifacts();
  }, [fetchArtifacts]);

  const current = artifacts[currentIdx] ?? null;
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < artifacts.length - 1;

  // Clip index if it exceeds array bounds (e.g., after accept removes last item)
  useEffect(() => {
    if (artifacts.length > 0 && currentIdx >= artifacts.length) {
      setCurrentIdx(artifacts.length - 1);
    }
  }, [artifacts.length, currentIdx]);

  // Fetch full detail when artifact changes
  const [currentDetail, setCurrentDetail] = useState<ReviewArtifact | null>(
    null
  );

  useEffect(() => {
    if (!current) {
      setCurrentDetail(null);
      return;
    }
    let cancelled = false;
    apiFetch(`/api/needs-review/${current.id}`)
      .then((res) => res.json())
      .then((json) => {
        if (!cancelled && json.data) {
          setCurrentDetail(json.data as ReviewArtifact);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [current?.id]);

  const handleSaveDraft = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await apiFetch(`/api/needs-review/${current.id}/draft`, {
        method: 'POST',
      });
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async () => {
    if (!current) return;
    const idx = currentIdx;
    setBusy(true);
    try {
      await apiFetch(`/api/needs-review/${current.id}/accept`, {
        method: 'POST',
      });
      setArtifacts((prev) => {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      });
      // If we just removed the last item, move index back
      if (idx >= artifacts.length - 1 && idx > 0) {
        setCurrentIdx(idx - 1);
      }
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const handleRequeue = async (stage: string) => {
    if (!current) return;
    setBusy(true);
    try {
      await apiFetch(`/api/needs-review/${current.id}/requeue`, {
        method: 'POST',
        body: JSON.stringify({ stage }),
      });
    } catch {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateField = async (field: string, value: unknown) => {
    if (!current) return;
    try {
      await apiFetch(`/api/needs-review/${current.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: value }),
      });
    } catch {
      // ignore
    }
  };

  if (userLoading) return null;
  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  const filterTabs: { key: ReviewTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'text', label: 'Text' },
    { key: 'attendees', label: 'Attendees' },
    { key: 'motions', label: 'Motions' },
    { key: 'financial', label: 'Financial' },
  ];

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Artifacts Review</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Review and manually correct extraction results flagged for review.
            {artifacts.length > 0 && (
              <span className="ml-1 font-medium text-gray-700">
                {artifacts.length} artifact
                {artifacts.length !== 1 ? 's' : ''} flagged.
              </span>
            )}
          </p>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          {filterTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
                filter === t.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {t.label}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={fetchArtifacts}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-[calc(100vh-13rem)] flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 gap-2">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <p className="text-sm">{error}</p>
            <Button size="sm" variant="outline" onClick={fetchArtifacts}>
              Retry
            </Button>
          </div>
        ) : artifacts.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
            <CheckCircle2 className="w-12 h-12 text-green-400" />
            <p className="text-sm font-medium text-gray-600">
              All clear — no artifacts need review
            </p>
            <p className="text-xs">
              {filter !== 'all'
                ? 'No items in this category. Try a different filter.'
                : 'All extractions passed quality checks.'}
            </p>
          </div>
        ) : currentIdx >= artifacts.length ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
            <CheckCircle2 className="w-12 h-12 text-green-400" />
            <p className="text-sm font-medium text-gray-600">
              This artifact has been submitted
            </p>
            <p className="text-xs">It is no longer in the review queue.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentIdx(0)}
              className="mt-2"
            >
              Go to first artifact
            </Button>
          </div>
        ) : (
          <>
            {/* ── Navigation bar ── */}
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
              <Button
                size="sm"
                variant="ghost"
                disabled={!hasPrev}
                onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <span className="text-sm text-gray-600 font-medium">
                {currentIdx + 1} of {artifacts.length}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={!hasNext}
                onClick={() =>
                  setCurrentIdx(Math.min(artifacts.length - 1, currentIdx + 1))
                }
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* ── Split panel ── */}
            <div className="flex-1 flex overflow-hidden">
              <div className="w-1/2 border-r border-gray-200">
                <DocumentPreview artifact={current} />
              </div>
              <div className="w-1/2 overflow-y-auto">
                <ExtractionPanel
                  key={current?.id ?? 'none'}
                  artifact={currentDetail ?? current}
                  onAccept={handleAccept}
                  onSaveDraft={handleSaveDraft}
                  onRequeue={handleRequeue}
                  onUpdateField={handleUpdateField}
                  busy={busy}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
